import {
  Button,
  Box,
  Card,
  HStack,
  IconButton,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseTrigger,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalPositioner,
  ModalTitle,
  Text,
} from "@commonsku/design-system";
import { XIcon } from "@commonsku/styles";
import React, { useState } from "react";
import { getBaseDomain } from "../../../../utils";
import { useDispatch, useSelector } from "react-redux";
import { selectCustomDomainData } from "../../../../redux/customDomain";
import { getCustomDomainStatus } from "../../../../actions/customDomain";

interface CustomDomainVerificationPopupProps {
  onClose: () => void;
  blocked?: boolean;
  shop_id: string;
}

const CustomDomainVerificationPopup = ({
  onClose,
  blocked = false,
  shop_id,
}: CustomDomainVerificationPopupProps) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const customDomain = useSelector(selectCustomDomainData);

  const handleVerification = async () => {
    setIsLoading(true);
    try {
      await dispatch(getCustomDomainStatus(shop_id));
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const isProduction = getBaseDomain() === "commonsku.com";
  const verificationContent = blocked ? (
    <Text>
      Confirm the CNAME and TXT records below match what's at your domain
      provider. If they already match, wait for DNS to update and try again, or
      contact support.
    </Text>
  ) : (
    <Text>
      To verify ownership of your domain, add these CNAME and TXT records at
      your domain provider. Once they're added, click Verify to complete the
      setup. Note: If you click Verify before adding the records - or before DNS
      has had time to propagate - verification will fail. You can retry any
      time.
    </Text>
  );

  return (
    <Modal
      closeOnEscape
      closeOnInteractOutside
      modal
      motionPreset="scale"
      placement="center"
      role="dialog"
      scrollBehavior="outside"
      size="lg"
      trapFocus
      open
    >
      <ModalBackdrop />
      <ModalPositioner>
        <ModalContent>
          <ModalHeader px="24px" pt="24px" pb="0">
            <ModalTitle
              fontSize="24px"
              fontWeight="semibold"
              className="modal-title-override"
              mb={1}
            >
              {blocked ? "Verification is blocked" : "Verify your domain"}
            </ModalTitle>
          </ModalHeader>
          <ModalBody pb="4">
            {verificationContent}
            <Card.Root variant="subtle" size="sm" mb="2">
              <Card.Body py="2">
                <HStack gap="8" justify="flex-start">
                  <Box>
                    <Text fontSize="xs" color="fg.muted">
                      Type
                    </Text>
                    <Text fontWeight="semibold" mb="0">
                      CNAME
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="fg.muted">
                      Value / Points to
                    </Text>
                    <Text fontWeight="semibold" mb="0">
                      {isProduction
                        ? "origin.commonskushops.com"
                        : "origin.commonskushopsdev.com"}
                    </Text>
                  </Box>
                </HStack>
              </Card.Body>
            </Card.Root>
            <Card.Root variant="subtle" size="sm">
              <Card.Body py="2">
                <HStack gap="2" justify="flex-start">
                  <Box>
                    <Text fontSize="xs" color="fg.muted">
                      Type
                    </Text>
                    <Text fontWeight="semibold" mb="0">
                      TXT
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="fg.muted">
                      Hostname
                    </Text>
                    <Text fontWeight="semibold" mb="0">
                      {customDomain?.ownershipVerification?.name}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="fg.muted">
                      Value
                    </Text>
                    <Text fontWeight="semibold" mb="0">
                      {customDomain?.ownershipVerification?.value}
                    </Text>
                  </Box>
                </HStack>
              </Card.Body>
            </Card.Root>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="customDomain-verification-popup-cancel.btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerification}
              disabled={isLoading}
              loading={isLoading}
              data-testid="customDomain-verification-popup-verify.btn"
            >
              Verify
            </Button>
          </ModalFooter>
          <ModalCloseTrigger asChild>
            <IconButton
              onClick={onClose}
              size="xl"
              label="Close"
              variant="ghost"
            >
              <XIcon />
            </IconButton>
          </ModalCloseTrigger>
        </ModalContent>
      </ModalPositioner>
    </Modal>
  );
};

export default CustomDomainVerificationPopup;
